import { useEffect, useMemo, useRef, useState } from "react";

/**
 * An answer that arrives whole, shown as it is written: the words type out
 * and each chart, table or card comes in once the words before it are done.
 *
 * Lucid composes an answer before it sends it, so without this the thread
 * jumped from "Working…" to a full screen at once. Only a live answer is
 * revealed; one read back from history, or for a reader who asked for less
 * motion, is shown whole.
 *
 * Every piece has a cost in characters: text its length, anything else a
 * short pause. The budget runs from 0 to the total over a time that grows
 * with the answer but stays short (0.6–2.8 s): a reveal, not a wait.
 */
const PAUSE = 80; // a chart or card costs as much as 80 characters of text
const MIN_MS = 600;
const MAX_MS = 2800;
const CHARS_PER_SEC = 900;

export const TEXT_KINDS = new Set(["text"]);

export function pieceCost(kind, text) {
  return TEXT_KINDS.has(kind) || kind === "headline" ? Math.max(1, String(text || "").length) : PAUSE;
}

/** Text cut at the budget, on a word's end, never mid-link: "[runs](trodo://…" would show as raw markdown. */
export function partialText(text, chars) {
  const s = String(text || "");
  if (chars >= s.length) return s;
  // Back to the last space, unless that is more than a long word away.
  let cut = s.lastIndexOf(" ", chars);
  if (cut <= 0 || chars - cut > 14) cut = chars;
  let out = s.slice(0, cut);
  const open = out.lastIndexOf("[");
  if (open > out.lastIndexOf(")") && open > out.lastIndexOf("]")) out = out.slice(0, open);
  return out;
}

/** How far a reveal that began at `from` and lasts `ms` has got: eased out, quick to start and settling at the end. */
function progress(from, ms, total) {
  const f = Math.min(1, (Date.now() - from) / ms);
  return { f, v: Math.round(total * (1 - (1 - f) ** 2)) };
}

function prefersLessMotion() {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (_) { return false; }
}

/**
 * @param {boolean|number} enabled  reveal this answer (a live one); a number is
 *   when it arrived, so a copy that replaces the live row carries on from there
 *   rather than starting again
 * @param {{kind: string, text?: string}[]} pieces  headline first, then the blocks in order
 * @returns {number|null} the budget spent so far, or null when everything shows
 */
export default function useAnswerReveal(enabled, pieces) {
  const total = useMemo(() => pieces.reduce((t, p) => t + pieceCost(p.kind, p.text), 0), [pieces]);
  const on = Boolean(enabled) && total > 0 && !prefersLessMotion();
  const ms = Math.min(MAX_MS, Math.max(MIN_MS, (total / CHARS_PER_SEC) * 1000));
  const from = useRef(typeof enabled === "number" ? enabled : Date.now());
  // Arrived long enough ago that the reveal would be over: shown whole.
  const live = on && Date.now() - from.current < ms;
  // A copy mounted mid-reveal starts where the reveal is, not blank.
  const [spent, setSpent] = useState(() => (live ? progress(from.current, ms, total).v : null));
  useEffect(() => {
    if (!live) { setSpent(null); return undefined; }
    let frame;
    const tick = () => {
      const { f, v } = progress(from.current, ms, total);
      setSpent(f >= 1 ? null : v);
      if (f < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [live, total, ms]);
  return live ? spent : null;
}
